/**
 * Sales Team Report renderer (Reports → New Vehicles).
 *
 * The Sales Funnel (Lead → Visit → Sale) grouped by sales team: one section
 * per location, one block per team (team name = sales manager's name;
 * Unassigned last), with per-team subtotals, per-location totals, and grand
 * totals across locations.
 *
 * Two modes, keyed off `data.periods`:
 *   RECENT — the original 11-column table over the last 5 working days.
 *   RANGE  — month-relative week columns for one selected metric, plus a
 *            whole-range total group. The forward outlook is unchanged.
 *
 * `periods` absent means a pre-range-mode payload, which renders as recent
 * mode — so deploying the backend before the frontend is safe.
 *
 * Row rendering is shared with the Forecast page's SalesFunnelTable via
 * components/shared/SalesFunnelRows so the numbers read the same everywhere.
 */

import React, { useState } from 'react';
import {
  FUNNEL_HISTORY_START,
  FUNNEL_METRIC_OPTIONS,
  FunnelMetricKey,
  SalesTeamFunnelReportData,
} from '../../../types/salesTeamFunnel';
import { formatLongDate, todayLocal } from '../../../utils/localDate';
import { TotalsRow } from '../../shared/SalesFunnelRows';
import { FunnelLocationSection } from './funnel/FunnelLocationSection';
import { FunnelWeekLocationSection } from './funnel/FunnelWeekLocationSection';
import { FunnelRangeControls } from './funnel/FunnelMetricSelect';
import {
  hasPeriods,
  printSizeBucket,
  weekModeActive,
} from './funnel/funnelWeekHelpers';
import { WeekTotalsRow } from './funnel/WeekFunnelRows';

function isCurrentMonth(reportDate: string): boolean {
  const now = new Date();
  const currentPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return reportDate.startsWith(currentPrefix);
}

export const SalesTeamFunnelReport: React.FC<{ data: SalesTeamFunnelReportData }> = ({ data }) => {
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());
  const [metric, setMetric] = useState<FunnelMetricKey>('sales');
  const [hideInactive, setHideInactive] = useState(false);

  const toggleRow = (rowId: string) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const isRange = hasPeriods(data);
  const weekMode = weekModeActive(data);
  const metricLabel = FUNNEL_METRIC_OPTIONS.find((o) => o.key === metric)!.label;

  // Active Leads is as-of-now, so its availability keys off the END date's
  // month in both modes.
  const historic = !isCurrentMonth(data.reportDate);
  const rangeStart = isRange ? data.rangeStartDate : data.reportDate;
  const preHistory = rangeStart < FUNNEL_HISTORY_START;
  const reportDateIsToday = data.reportDate === todayLocal();
  const anyPartial = isRange && data.periods.some((p) => p.partial);

  return (
    <div className={weekMode ? `funnel-print-${printSizeBucket(data.periods!.length)}` : undefined}>
      {historic && (
        <div className="mb-4 px-3 py-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded">
          {isRange
            ? 'Active Leads is a point-in-time count as of today and does not correspond to the '
              + 'selected date range; for a range ending in a past month it shows as —.'
            : 'Historic report date: Active Leads is a point-in-time count and is not available for '
              + 'past months (shown as —).'}
          {preHistory && (
            <> Lead/visit history begins {formatLongDate(FUNNEL_HISTORY_START)} — leads and visits
            before that date show as zero.</>
          )}
        </div>
      )}

      {isRange && data.periods.length === 1 && (
        <div className="mb-4 px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded">
          This range falls inside a single week bucket ({data.periods[0].sublabel}), so the weekly
          columns are hidden — they would repeat the range totals. Widen the range to compare weeks.
        </div>
      )}

      {/* Gated on isRange, NOT weekMode: the inactivity filter applies to any
          range, including one that collapsed to a single week bucket. Only the
          metric selector depends on week columns existing. */}
      {isRange && (
        <FunnelRangeControls
          showMetricSelect={weekMode}
          metric={metric}
          onMetricChange={setMetric}
          hideInactive={hideInactive}
          onHideInactiveChange={setHideInactive}
          periodCount={data.periods.length}
        />
      )}

      {/* Print-only restatement of the controls, which are print:hidden. */}
      {isRange && (weekMode || hideInactive) && (
        <div className="hidden print:block mb-2 text-xs text-gray-600">
          {weekMode && `Weekly columns: ${metricLabel}`}
          {weekMode && hideInactive && ' · '}
          {hideInactive && 'salespeople with no activity in the range hidden'}
        </div>
      )}

      {data.locations.map((location) => (
        weekMode && isRange ? (
          <FunnelWeekLocationSection
            key={location.locationId}
            location={location}
            periods={data.periods}
            metric={metric}
            metricLabel={metricLabel}
            rangeStartDate={data.rangeStartDate}
            rangeEndDate={data.rangeEndDate}
            openRows={openRows}
            onToggle={toggleRow}
            activeLeadsUnavailable={historic}
            reportDateIsToday={reportDateIsToday}
            hideInactive={hideInactive}
          />
        ) : (
          <FunnelLocationSection
            key={location.locationId}
            location={location}
            openRows={openRows}
            onToggle={toggleRow}
            activeLeadsUnavailable={historic}
            reportDate={data.reportDate}
            reportDateIsToday={reportDateIsToday}
            // A range that collapsed to one bucket still renders here, so it
            // must keep RANGE labels — otherwise calendar-range totals would be
            // headed "Last 5 Working Days".
            range={isRange
              ? { startDate: data.rangeStartDate, endDate: data.rangeEndDate }
              : undefined}
            hideInactive={isRange && hideInactive}
          />
        )
      ))}

      {data.locations.length > 1 && (
        <div className={`overflow-x-auto border border-gray-300 rounded-lg mb-4${
          weekMode ? ' funnel-week-scroller' : ''}`}>
          {/* In week mode the grand total carries its own <thead>: two
              independent horizontal scrollers cannot stay column-aligned once
              there are 16 extra columns, so it reads as its own roll-up table. */}
          {weekMode && isRange ? (
            <table className="min-w-full w-auto text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left min-w-[200px] max-w-[260px] shadow-[inset_-1px_0_0_0_rgb(229,231,235)]">
                    Company Roll-up
                  </th>
                  {data.periods.map((period, i) => (
                    <th
                      key={period.key}
                      className={`px-2 py-2 text-right whitespace-nowrap ${i === 0 ? 'border-l border-gray-200' : ''}`}
                    >
                      {period.label}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right border-l border-gray-200">Leads</th>
                  <th className="px-3 py-2 text-right">Visits</th>
                  <th className="px-3 py-2 text-right">Appts</th>
                  <th className="px-3 py-2 text-right">Sales</th>
                  <th className="px-3 py-2 text-right border-l border-gray-200 funnel-print-optional">Visits/Lead</th>
                  <th className="px-3 py-2 text-right funnel-print-optional">Sales/Visit</th>
                  <th className="px-3 py-2 text-right funnel-print-optional">Sales/Lead</th>
                  <th className="px-3 py-2 text-right border-l border-gray-200">Active Leads</th>
                  <th className="px-3 py-2 text-right border-l border-gray-200 funnel-print-optional">Next 3</th>
                  <th className="px-3 py-2 text-right funnel-print-optional">Next 5</th>
                </tr>
              </thead>
              <tbody>
                <WeekTotalsRow
                  label={`All Selected Locations (${data.locations.length})`}
                  totals={data.grandTotals}
                  periods={data.periods}
                  metric={metric}
                  emphasis
                  activeLeadsUnavailable={historic}
                />
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                <TotalsRow
                  label={`All Selected Locations (${data.locations.length})`}
                  totals={data.grandTotals}
                  emphasis
                  activeLeadsUnavailable={historic}
                />
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="px-1 py-2 text-xs text-gray-500">
        {anyPartial && (
          <>
            <span className="text-amber-500">*</span> Partial week — the bucket does not cover a
            full 7 days because the date range starts or ends inside it (the last bucket may still
            be in progress).{' '}
          </>
        )}
        Teams are configured in Configuration → Sales Teams; salespeople without a team appear under
        Unassigned.{' '}
        {!isRange && (
          <>
            Last-5 working days follow each location's NEW-vehicle schedule (Sundays/holidays
            excluded).
          </>
        )}
        {isRange && weekMode && (
          <>
            Range Total covers every calendar day from {data.rangeStartDate} through{' '}
            {data.rangeEndDate}. Weeks are grouped by month: W1 = the 1st–7th, W2 = 8th–14th,
            W3 = 15th–21st, W4 = 22nd–28th, W5 = 29th–month end, so a bucket never spans two
            months. Week columns show one metric at a time; Range Total always sums all four.
          </>
        )}
        {isRange && !weekMode && (
          <>
            Range Total covers every calendar day from {data.rangeStartDate} through{' '}
            {data.rangeEndDate} — calendar days, not working days.
          </>
        )}{' '}
        Next 3 / Next 5 are calendar days from the {isRange ? 'end date' : 'report date'}, matching
        the Sales Appointments 5-day outlook. Leads: VinSolutions Good Leads by origination date.
        Visits: completed showroom visits credited to the customer's salesperson (be-backs count).
        Sales: retail units from DealerBuilt (wholesale/swap excluded; 2-salesperson deals credit
        each rep ½ unit). Active Leads: leads in Active status from the last 90 days (current month
        only).{' '}
        {isRange && data.apptsPastWindow && (
          <>
            Appts Scheduled includes the end date, so the Appts figures — including the final week
            column — cover {data.apptsPastWindow.startDate} through {data.apptsPastWindow.endDate}
            {' '}and appointments on {data.rangeEndDate} are counted under Next 3 / Next 5 instead.
          </>
        )}
        {isRange && !data.apptsPastWindow && (
          <>
            This range is a single day, so every appointment on {data.rangeEndDate} is counted
            under Next 3 / Next 5 and the Appts column is empty by definition.
          </>
        )}
        {!isRange && (
          <>
            Appts Scheduled includes the report date — appointments on the report date count there,
            not in the Last-5 Appts column.
          </>
        )}{' '}
        Rows with a ▸ expand to show scheduled appointment times.
      </div>
    </div>
  );
};
